import React, { createContext, useContext, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Button } from "../design/button";
import { useDiscoveryRequestTask } from "../contexts/DiscoveryRequestTaskContext";
import { Field, FieldGroup, Fieldset, Label } from "../design/fieldset";
import { Input } from "../design/input";
import { Textarea } from "../design/textarea";
import { DiscoveryRequest } from "../models/DiscoveryRequest";

interface Context {
  openDialog: (discoveryRequestId: DiscoveryRequest["_id"]) => void;
  closeDialog: () => void;
  isOpen: boolean;
}

const CreateDiscoveryRequestTaskDialogProviderContext = createContext<
  Context | undefined
>(undefined);

export const CreateDiscoveryRequestTaskDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { createDiscoveryRequestTask } = useDiscoveryRequestTask();
  const [discoveryRequestId, setDiscoveryRequestId] = useState<
    DiscoveryRequest["_id"] | undefined
  >(undefined);
  const [isOpen, setIsOpen] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const openDialog = (discoveryRequestId: DiscoveryRequest["_id"]) => {
    setDiscoveryRequestId(discoveryRequestId);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setDiscoveryRequestId(undefined);
  };

  const handleYes = async () => {
    if (!discoveryRequestId) return;
    try {
      await createDiscoveryRequestTask({
        title,
        description,
        isCompleted: false,
        discoveryRequestId: discoveryRequestId,
        responses: [],
      });
      closeDialog();
    } catch (error) {
      console.error("Error completing discoveryRequestTask:", error);
      alert("Failed to complete discoveryRequestTask. Please try again later.");
    }
  };

  return (
    <CreateDiscoveryRequestTaskDialogProviderContext.Provider
      value={{ openDialog, closeDialog, isOpen }}
    >
      {discoveryRequestId && (
        <Dialog open={isOpen} onClose={closeDialog}>
          <DialogTitle>Create Task</DialogTitle>
          <DialogBody>
            <Fieldset>
              <FieldGroup>
                <Field>
                  <Label>Title</Label>
                  <Input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter task title"
                  />
                </Field>
                <Field>
                  <Label>Description</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter task description"
                  />
                </Field>
              </FieldGroup>
            </Fieldset>
          </DialogBody>
          <DialogActions>
            <Button plain onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={handleYes}>Save</Button>
          </DialogActions>
        </Dialog>
      )}
      {children}
    </CreateDiscoveryRequestTaskDialogProviderContext.Provider>
  );
};

export const useCreateDiscoveryRequestTaskDialog = () => {
  const ctx = useContext(CreateDiscoveryRequestTaskDialogProviderContext);
  if (!ctx)
    throw new Error(
      "useCreateDiscoveryRequestTaskDialog must be used inside CreateDiscoveryRequestTaskDialogProvider",
    );
  return ctx;
};
